import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2 } from "lucide-react";

export default function BookingLookup() {
  const navigate = useNavigate();
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const ref = reference.trim().toUpperCase();
    if (!ref) return toast.error("Enter your booking reference");
    setLoading(true);
    try {
      await api.get(`/bookings/reference/${ref}`);
      navigate(`/booking/${ref}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Booking not found. Check the reference and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--sand)] flex flex-col" data-testid="booking-lookup-page">
      <nav className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[color:var(--brand)]/10 bg-white">
        <Link to="/" className="flex items-center gap-2 text-[color:var(--deep)]" data-testid="lookup-back-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-ui text-sm">Back to Baraka Inn</span>
        </Link>
        <Link to="/admin/login" className="font-ui text-xs sm:text-sm px-4 py-2 rounded-full bg-[color:var(--deep)] text-white hover:bg-[color:var(--brand-dark)] transition-colors">Staff</Link>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-[color:var(--brand)]/10 p-8 sm:p-10">
          <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)]">Returning guest</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[color:var(--deep)] mt-2">Find your booking</h1>
          <p className="mt-3 font-body text-sm text-[color:var(--muted-text)]">Enter the reference we sent you (e.g. <span className="font-mono">BR12AB34</span>) to view your booking, pay online, or check status.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="booking-lookup-form">
            <div>
              <Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Booking reference</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="BR12AB34"
                data-testid="lookup-reference-input"
                className="mt-1 font-mono uppercase tracking-wider"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="lookup-submit-btn" className="w-full h-11 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)] text-white font-ui rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Find my booking</>}
            </Button>
          </form>

          <p className="mt-6 text-center font-ui text-xs text-[color:var(--muted-text)]">Lost your reference? Call reception — the number is on our <Link to="/" className="underline text-[color:var(--brand)]">homepage</Link>.</p>
        </div>
      </div>
    </div>
  );
}
